import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader, ConfirmDialog } from 'components/modals';
import { SyutSelect, SyutTreeGrid, type TreeGridColumn } from 'components/ui';
import notificationService from '../../../services/notificationService';
import { roleService, permissionService, authService } from 'api/services';
import type {
  RolePermissionTreeDto,
  ModulePermissionNodeDto,
  PermissionAssignment,
  PermissionActionDto,
  ResourcePermissionNodeDto,
  UserPermissionTreeDto,
  UserEffectivePermissionsDto,
  EffectivePermissionActionDto,
  EffectiveModuleNodeDto,
  EffectiveResourceNodeDto,
  PermissionSource,
} from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect/SyutSelectUnified';
import '../config-module/config-module.css';

// ============================================================================
// TYPES
// ============================================================================

type ScreenMode = 'role' | 'user-override' | 'effective';

// ============================================================================
// RESOURCE TYPE CATEGORIES
// ============================================================================

interface ResourceTypeCategory {
  key: string;        // 'MODULE', 'MENU', 'SCREEN', 'ENTITY', etc.
  label: string;      // 'Modules', 'Menus', etc.
  group: 'ui' | 'business';
  icon: string;       // IconSystem key
}

const UI_CATEGORIES: ResourceTypeCategory[] = [
  { key: 'MODULE', label: 'Modules',  group: 'ui', icon: 'folder' },
  { key: 'MENU',   label: 'Menus',    group: 'ui', icon: 'menu' },
  { key: 'SCREEN', label: 'Screens',  group: 'ui', icon: 'grid' },
];

const RESOURCE_TYPE_DISPLAY: Record<string, { label: string; icon: string }> = {
  ENTITY:         { label: 'Entities',        icon: 'users' },
  BRANCH:         { label: 'Branches',        icon: 'list' },
  DEPARTMENT:     { label: 'Departments',     icon: 'folder' },
  FINANCIAL_YEAR: { label: 'Financial Years', icon: 'calendar' },
};

function deriveBusinessCategories(resources?: ResourcePermissionNodeDto[] | EffectiveResourceNodeDto[]): ResourceTypeCategory[] {
  if (!resources || resources.length === 0) return [];
  const seen = new Set<string>();
  const cats: ResourceTypeCategory[] = [];
  for (const res of resources) {
    if (seen.has(res.resourceType)) continue;
    seen.add(res.resourceType);
    const display = RESOURCE_TYPE_DISPLAY[res.resourceType] || { label: res.resourceType, icon: 'settings' };
    cats.push({ key: res.resourceType, label: display.label, group: 'business', icon: display.icon });
  }
  return cats.sort((a, b) => a.label.localeCompare(b.label));
}

// ============================================================================
// PERMISSION TOGGLE
// ============================================================================

interface PermissionToggleProps {
  checked: boolean | null;   // true=granted, false=denied, null=not-set
  onChange: (checked: boolean | null) => void;
  disabled?: boolean;
  sourceBadge?: { label: string; color: string } | null;
  size?: 'sm' | 'md';
}

const PermissionToggle: React.FC<PermissionToggleProps> = ({ checked, onChange, disabled, sourceBadge, size = 'sm' }) => {
  const isOn = checked === true;
  const w = size === 'md' ? 36 : 28;
  const h = size === 'md' ? 20 : 14;
  const knob = size === 'md' ? 16 : 10;
  const pad = 2;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.125rem' }}>
      <div
        onClick={disabled ? undefined : () => onChange(isOn ? null : true)}
        title={checked === true ? 'Granted' : checked === false ? 'Explicitly Denied' : 'Not Set'}
        style={{
          width: `${w}px`, height: `${h}px`, borderRadius: `${h / 2}px`,
          boxSizing: 'border-box',
          border: `1px solid ${checked === true ? 'var(--primary-dark, #2563eb)' : checked === false ? 'var(--danger-dark, #dc2626)' : 'var(--border-color-strong, #94a3b8)'}`,
          cursor: disabled ? 'default' : 'pointer',
          background: checked === true ? 'var(--primary)' : checked === false ? 'var(--danger)' : 'var(--border-color)',
          position: 'relative', transition: 'background 0.2s, border-color 0.2s',
          opacity: disabled ? 0.7 : 1, flexShrink: 0,
        }}
      >
        <div style={{
          width: `${knob}px`, height: `${knob}px`, borderRadius: '50%', background: '#fff',
          position: 'absolute', top: `${pad}px`,
          left: isOn ? `${w - knob - pad}px` : `${pad}px`,
          transition: 'left 0.2s',
          boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
        }} />
      </div>
      {sourceBadge && (
        <span style={{
          fontSize: '0.5rem', color: sourceBadge.color, textAlign: 'center',
          lineHeight: 1.1, fontWeight: 600, maxWidth: '52px', overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} title={sourceBadge.label}>
          {sourceBadge.label}
        </span>
      )}
    </div>
  );
};

// ============================================================================
// PERMISSION TREE ROW HELPERS
// ============================================================================

const ALL_ACTION_CODES = ['VIEW', 'CREATE', 'UPDATE', 'DELETE', 'EXPORT', 'IMPORT', 'APPROVE', 'REJECT', 'PRINT', 'MANAGE'];

// ============================================================================
// FLAT ROW TYPE FOR SYUT TREE GRID
// ============================================================================

interface PermissionRow {
  id: string;
  parentId: string | null;
  resourceType: string;       // 'MODULE' | 'MENU' | 'SCREEN' | 'ELEMENT' | 'ENTITY' | 'BRANCH' | etc.
  resourceId: string;
  code: string;
  name: string;
  sortOrder: number;
  moduleId?: string;          // which module this row belongs to (for menu/screen/element filtering)
  elementType?: string;
  stdActions?: PermissionActionDto[];
  effActions?: EffectivePermissionActionDto[];
}

function flattenStandardTree(modules: ModulePermissionNodeDto[], resources?: ResourcePermissionNodeDto[]): PermissionRow[] {
  const rows: PermissionRow[] = [];
  const modId = (mod: ModulePermissionNodeDto) => String(mod.moduleId);
  // UI hierarchy: Module → Menu → Screen → Element
  modules.forEach((mod, mi) => {
    const modKey = `mod-${mod.moduleId}`;
    const mid = modId(mod);
    const menuIdSet = new Set(mod.menus.map(m => String(m.menuId)));
    rows.push({ id: modKey, parentId: null, resourceType: 'MODULE', resourceId: mid, code: mod.moduleCode, name: mod.moduleName, sortOrder: mi, moduleId: mid, stdActions: mod.actions });
    mod.menus.forEach((menu, mni) => {
      const menuKey = `menu-${menu.menuId}`;
      const parentKey = menu.parentMenuId && menuIdSet.has(String(menu.parentMenuId))
        ? `menu-${menu.parentMenuId}`
        : modKey;
      rows.push({ id: menuKey, parentId: parentKey, resourceType: 'MENU', resourceId: String(menu.menuId), code: menu.menuCode, name: menu.menuName, sortOrder: mni, moduleId: mid, stdActions: menu.actions });
      menu.screens.forEach((screen, si) => {
        const scrKey = `scr-${screen.screenId}`;
        rows.push({ id: scrKey, parentId: menuKey, resourceType: 'SCREEN', resourceId: String(screen.screenId), code: screen.screenCode, name: screen.screenName, sortOrder: si, moduleId: mid, stdActions: screen.actions });
        screen.elements.forEach((el, ei) => {
          rows.push({ id: `el-${el.elementId}`, parentId: scrKey, resourceType: 'ELEMENT', resourceId: String(el.elementId), code: el.elementCode, name: el.elementName, sortOrder: ei, moduleId: mid, elementType: el.elementType, stdActions: el.actions });
        });
      });
    });
  });
  // Business resources with parentResourceId support
  if (resources) {
    resources.forEach((res) => {
      const parentId = res.parentResourceId
        ? `res-${res.resourceType}-${res.parentResourceId}`
        : null;
      rows.push({ id: `res-${res.resourceType}-${res.resourceId}`, parentId, resourceType: res.resourceType, resourceId: String(res.resourceId), code: res.resourceCode, name: res.resourceName, sortOrder: res.sortOrder, stdActions: res.actions });
    });
  }
  return rows;
}

function flattenEffectiveTree(modules: EffectiveModuleNodeDto[], resources?: EffectiveResourceNodeDto[]): PermissionRow[] {
  const rows: PermissionRow[] = [];
  modules.forEach((mod, mi) => {
    const modKey = `mod-${mod.moduleId}`;
    const mid = String(mod.moduleId);
    const menuIdSet = new Set(mod.menus.map(m => String(m.menuId)));
    rows.push({ id: modKey, parentId: null, resourceType: 'MODULE', resourceId: mid, code: mod.moduleCode, name: mod.moduleName, sortOrder: mi, moduleId: mid, effActions: mod.actions });
    mod.menus.forEach((menu, mni) => {
      const menuKey = `menu-${menu.menuId}`;
      const parentKey = menu.parentMenuId && menuIdSet.has(String(menu.parentMenuId))
        ? `menu-${menu.parentMenuId}`
        : modKey;
      rows.push({ id: menuKey, parentId: parentKey, resourceType: 'MENU', resourceId: String(menu.menuId), code: menu.menuCode, name: menu.menuName, sortOrder: mni, moduleId: mid, effActions: menu.actions });
      menu.screens.forEach((screen, si) => {
        const scrKey = `scr-${screen.screenId}`;
        rows.push({ id: scrKey, parentId: menuKey, resourceType: 'SCREEN', resourceId: String(screen.screenId), code: screen.screenCode, name: screen.screenName, sortOrder: si, moduleId: mid, effActions: screen.actions });
        screen.elements.forEach((el, ei) => {
          rows.push({ id: `el-${el.elementId}`, parentId: scrKey, resourceType: 'ELEMENT', resourceId: String(el.elementId), code: el.elementCode, name: el.elementName, sortOrder: ei, moduleId: mid, elementType: el.elementType, effActions: el.actions });
        });
      });
    });
  });
  if (resources) {
    resources.forEach((res) => {
      const parentId = res.parentResourceId
        ? `res-${res.resourceType}-${res.parentResourceId}`
        : null;
      rows.push({ id: `res-${res.resourceType}-${res.resourceId}`, parentId, resourceType: res.resourceType, resourceId: String(res.resourceId), code: res.resourceCode, name: res.resourceName, sortOrder: res.sortOrder, effActions: res.actions });
    });
  }
  return rows;
}

function getActionValue(actions: { actionCode: string; permissionId: string | null; isAllowed: boolean | null }[], code: string): boolean | null {
  const a = actions.find(x => x.actionCode === code);
  if (!a || a.permissionId === null) return null;
  return a.isAllowed;
}

// Source badge for effective view
function getSourceBadge(source: PermissionSource, roleNames?: string[]): { label: string; color: string } {
  switch (source) {
    case 'user-override': return { label: 'User Override', color: 'var(--info, #6f42c1)' };
    case 'role-grant': return { label: roleNames?.length ? `Role: ${roleNames.join(', ')}` : 'Role Grant', color: 'var(--primary)' };
    case 'role-deny': return { label: roleNames?.length ? `Deny: ${roleNames.join(', ')}` : 'Role Deny', color: 'var(--danger)' };
    case 'default-deny': return { label: 'Default', color: 'var(--text-secondary)' };
    default: return { label: '', color: 'var(--text-secondary)' };
  }
}

// ============================================================================
// MODE OPTIONS
// ============================================================================

const MODE_OPTIONS: { key: ScreenMode; label: string }[] = [
  { key: 'role', label: 'Role Permissions' },
  { key: 'user-override', label: 'User Overrides' },
  { key: 'effective', label: 'Effective View' },
];

const MODE_SELECT_OPTIONS: SelectOption[] = MODE_OPTIONS.map(opt => ({
  value: opt.key,
  label: opt.label,
}));

// ============================================================================
// PERMISSION ASSIGNMENT SCREEN
// ============================================================================

interface PermissionAssignmentScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const PermissionAssignmentScreen: React.FC<PermissionAssignmentScreenProps> = ({ moduleIcon }) => {
  // Mode
  const [mode, setMode] = useState<ScreenMode>('role');

  // Selection
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  // Tree data
  const [roleTree, setRoleTree] = useState<RolePermissionTreeDto | null>(null);
  const [userTree, setUserTree] = useState<UserPermissionTreeDto | null>(null);
  const [effectiveData, setEffectiveData] = useState<UserEffectivePermissionsDto | null>(null);
  const [loadingTree, setLoadingTree] = useState(false);
  const [saving, setSaving] = useState(false);

  // Draft state: key = `${resourceType}:${resourceId}:${actionCode}` → boolean | null
  const [draft, setDraft] = useState<Map<string, boolean | null>>(new Map());

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: 'switch' | 'discard' | 'save';
    targetCategory?: string;
  }>({ open: false, type: 'discard' });

  // Reset state when mode changes
  const handleModeChange = useCallback((newMode: ScreenMode) => {
    setMode(newMode);
    setDraft(new Map());
  }, []);

  // Load roles for dropdown
  const loadRoleOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await roleService.getAll({
      searchTerm: inputValue || undefined,
      pageSize: inputValue ? 50 : 200,
      includeInactive: false,
    });
    return res.data.map(r => ({ value: String(r.id), label: `${r.roleCode} — ${r.roleName}` }));
  }, []);

  // Load users for dropdown
  const loadUserOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await authService.getUsers({
      searchTerm: inputValue || undefined,
      pageSize: inputValue ? 50 : 200,
    });
    return res.data.map(u => ({ value: String(u.id), label: `${u.fullName} (${u.username})` }));
  }, []);

  // Category panel state
  const [selectedCategory, setSelectedCategory] = useState<string>('MODULE');
  const [menuModuleFilter, setMenuModuleFilter] = useState<string>('');

  // Reset draft on new data load
  const resetTreeState = useCallback(() => {
    setDraft(new Map());
    setSelectedCategory('MODULE');
    setMenuModuleFilter('');
  }, []);

  // Load role permission tree
  const loadRoleTree = useCallback(async (roleId: string) => {
    if (!roleId) return;
    setLoadingTree(true);
    resetTreeState();
    try {
      const res = await permissionService.getRolePermissionTree(roleId);
      setRoleTree(res.data);
    } catch {
      notificationService.toast.error('Failed to load permission tree');
    } finally {
      setLoadingTree(false);
    }
  }, [resetTreeState]);

  // Load user permission override tree
  const loadUserTree = useCallback(async (userId: string) => {
    if (!userId) return;
    setLoadingTree(true);
    resetTreeState();
    try {
      const res = await permissionService.getUserPermissionTree(userId);
      setUserTree(res.data);
    } catch {
      notificationService.toast.error('Failed to load user permission overrides');
    } finally {
      setLoadingTree(false);
    }
  }, [resetTreeState]);

  // Load effective permissions
  const loadEffective = useCallback(async (userId: string) => {
    if (!userId) return;
    setLoadingTree(true);
    resetTreeState();
    try {
      const res = await permissionService.getUserEffectivePermissions(userId);
      setEffectiveData(res.data);
    } catch {
      notificationService.toast.error('Failed to load effective permissions');
    } finally {
      setLoadingTree(false);
    }
  }, [resetTreeState]);

  // Effect: load data when selection or mode changes
  useEffect(() => {
    if (mode === 'role') {
      if (selectedRoleId) loadRoleTree(selectedRoleId);
      else setRoleTree(null);
    } else if (mode === 'user-override') {
      if (selectedUserId) loadUserTree(selectedUserId);
      else setUserTree(null);
    } else if (mode === 'effective') {
      if (selectedUserId) loadEffective(selectedUserId);
      else setEffectiveData(null);
    }
  }, [mode, selectedRoleId, selectedUserId, loadRoleTree, loadUserTree, loadEffective]);

  // Draft management
  const getDraftKey = (resourceType: string, resourceId: string, actionCode: string) =>
    `${resourceType}:${resourceId}:${actionCode}`;

  const getEffectiveValue = useCallback((
    resourceType: string,
    resourceId: string,
    actionCode: string,
    originalActions: { actionCode: string; permissionId: string | null; isAllowed: boolean | null; actionId: string }[],
  ): boolean | null => {
    const key = getDraftKey(resourceType, resourceId, actionCode);
    if (draft.has(key)) return draft.get(key)!;
    return getActionValue(originalActions, actionCode);
  }, [draft]);

  const setDraftValue = useCallback((
    resourceType: string,
    resourceId: string,
    actionCode: string,
    value: boolean | null,
    originalActions: { actionCode: string; permissionId: string | null; isAllowed: boolean | null }[],
  ) => {
    setDraft(prev => {
      const next = new Map(prev);
      const key = getDraftKey(resourceType, resourceId, actionCode);
      const originalValue = getActionValue(originalActions, actionCode);
      if (value === originalValue) {
        next.delete(key); // Reverted to original — not a real change
      } else {
        next.set(key, value);
      }
      return next;
    });
  }, []);

  // Map resource type → category (ELEMENT rolls into SCREEN)
  const toCat = (rt: string) => rt === 'ELEMENT' ? 'SCREEN' : rt;

  // Collect assignments only for the selected category (with draft applied)
  const buildAssignments = useCallback((
    modules: ModulePermissionNodeDto[],
    resources: ResourcePermissionNodeDto[] | undefined,
    saveCategory: string,
  ): { assignments: PermissionAssignment[]; resourceTypes: string[] } => {
    const assignments: PermissionAssignment[] = [];

    const processActions = (
      actions: { actionCode: string; permissionId: string | null; actionId: string; isAllowed: boolean | null }[],
      resourceType: string,
      resourceId: string,
    ) => {
      for (const a of actions) {
        const key = getDraftKey(resourceType, resourceId, a.actionCode);
        const effectiveValue = draft.has(key) ? draft.get(key)! : a.isAllowed;
        if (effectiveValue !== null) {
          assignments.push({
            permissionId: a.permissionId,
            actionId: a.actionId,
            resourceType,
            resourceId,
            isAllowed: effectiveValue,
          });
        }
      }
    };

    // Resource types that will be sent (backend scopes delete to these)
    let resourceTypes: string[];

    if (saveCategory === 'MODULE') {
      resourceTypes = ['MODULE'];
      for (const mod of modules) {
        processActions(mod.actions as any, 'MODULE', String(mod.moduleId));
      }
    } else if (saveCategory === 'MENU') {
      resourceTypes = ['MENU'];
      for (const mod of modules) {
        for (const menu of mod.menus) {
          processActions(menu.actions as any, 'MENU', String(menu.menuId));
        }
      }
    } else if (saveCategory === 'SCREEN') {
      resourceTypes = ['SCREEN', 'ELEMENT'];
      for (const mod of modules) {
        for (const menu of mod.menus) {
          for (const screen of menu.screens) {
            processActions(screen.actions as any, 'SCREEN', String(screen.screenId));
            for (const el of screen.elements) {
              processActions(el.actions as any, 'ELEMENT', String(el.elementId));
            }
          }
        }
      }
    } else {
      // Business resource category
      resourceTypes = [saveCategory];
      if (resources) {
        for (const res of resources) {
          if (res.resourceType === saveCategory) {
            processActions(res.actions as any, res.resourceType, String(res.resourceId));
          }
        }
      }
    }

    return { assignments, resourceTypes };
  }, [draft]);

  // Clear only draft entries belonging to a specific category
  const clearCategoryDraft = useCallback((category: string) => {
    setDraft(prev => {
      const next = new Map(prev);
      for (const key of prev.keys()) {
        if (toCat(key.split(':')[0]) === category) next.delete(key);
      }
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const cat = selectedCategory;
      if (mode === 'role' && selectedRoleId && roleTree) {
        const { assignments, resourceTypes } = buildAssignments(roleTree.modules, roleTree.resources, cat);
        await permissionService.bulkAssign(selectedRoleId, { assignments, resourceTypes });
        notificationService.toast.success('Permissions saved successfully');
        // Clear only saved category's drafts, then reload tree (preserving other drafts)
        clearCategoryDraft(cat);
        const res = await permissionService.getRolePermissionTree(selectedRoleId);
        setRoleTree(res.data);
      } else if (mode === 'user-override' && selectedUserId && userTree) {
        const { assignments, resourceTypes } = buildAssignments(userTree.modules, userTree.resources, cat);
        await permissionService.bulkAssignUserPermissions(selectedUserId, { assignments, resourceTypes });
        notificationService.toast.success('User overrides saved successfully');
        clearCategoryDraft(cat);
        const res = await permissionService.getUserPermissionTree(selectedUserId);
        setUserTree(res.data);
      }
    } catch (err: any) {
      const respData = err?.response?.data;
      const msg = respData?.message || respData?.title || err?.message || 'Unknown error';
      console.error('Permission save failed:', err, 'Response:', respData);
      notificationService.toast.error(`Failed to save permissions: ${msg}`);
    } finally {
      setSaving(false);
    }
  }, [mode, selectedRoleId, selectedUserId, roleTree, userTree, selectedCategory, buildAssignments, clearCategoryDraft]);

  const isEffectiveMode = mode === 'effective';

  // Current active tree modules + resources
  const activeModules = useMemo((): ModulePermissionNodeDto[] | null => {
    if (mode === 'role') return roleTree?.modules ?? null;
    if (mode === 'user-override') return userTree?.modules ?? null;
    return null;
  }, [mode, roleTree, userTree]);

  const activeResources = useMemo((): ResourcePermissionNodeDto[] | undefined => {
    if (mode === 'role') return roleTree?.resources;
    if (mode === 'user-override') return userTree?.resources;
    return undefined;
  }, [mode, roleTree, userTree]);

  const effectiveModules = isEffectiveMode ? effectiveData?.modules ?? null : null;
  const effectiveResources = isEffectiveMode ? effectiveData?.resources : undefined;

  // Whether we have a selection to show the tree
  const hasSelection = mode === 'role' ? !!selectedRoleId : !!selectedUserId;

  // ============================================================================
  // CATEGORY PANEL MEMOS
  // ============================================================================

  const allCategories = useMemo((): ResourceTypeCategory[] => {
    const bizResources = isEffectiveMode ? effectiveResources : activeResources;
    return [...UI_CATEGORIES, ...deriveBusinessCategories(bizResources)];
  }, [isEffectiveMode, effectiveResources, activeResources]);

  const MENU_MODULE_ALL = '__ALL__';

  const moduleOptions = useMemo((): SelectOption[] => {
    const modules = isEffectiveMode ? effectiveModules : activeModules;
    if (!modules) return [];
    const filtered = (modules as Array<any>)
      .filter(m => {
        const mid = String(m.moduleId);
        if (isEffectiveMode) {
          return m.actions?.some((a: any) => a.effectiveValue === true);
        }
        const actions = m.actions as PermissionActionDto[] | undefined;
        if (!actions || actions.length === 0) return false;
        return actions.some((a: PermissionActionDto) => {
          const key = getDraftKey('MODULE', mid, a.actionCode);
          if (draft.has(key)) return draft.get(key) === true;
          return a.isAllowed === true;
        });
      })
      .map(m => ({ value: String(m.moduleId), label: `${m.moduleCode} — ${m.moduleName}` }));
    if (filtered.length === 0) return [];
    return [{ value: MENU_MODULE_ALL, label: 'All Modules' }, ...filtered];
  }, [isEffectiveMode, effectiveModules, activeModules, draft]);

  // Auto-clear menu module filter if the selected module is no longer assigned
  useEffect(() => {
    if (menuModuleFilter && moduleOptions.length > 0 && !moduleOptions.some(o => o.value === menuModuleFilter)) {
      setMenuModuleFilter('');
    }
  }, [menuModuleFilter, moduleOptions]);

  // ============================================================================
  // TREE GRID DATA + COLUMNS
  // ============================================================================

  // Flatten nested tree for SyutTreeGrid
  const flattenedData = useMemo((): PermissionRow[] => {
    if (isEffectiveMode && effectiveModules) return flattenEffectiveTree(effectiveModules, effectiveResources);
    if (activeModules) return flattenStandardTree(activeModules, activeResources);
    return [];
  }, [isEffectiveMode, effectiveModules, effectiveResources, activeModules, activeResources]);

  // Count items per category for sidebar badges
  const categoryCounts = useMemo((): Record<string, number> => {
    const counts: Record<string, number> = {};
    for (const row of flattenedData) {
      if (row.resourceType === 'MODULE') counts['MODULE'] = (counts['MODULE'] || 0) + 1;
      else if (row.resourceType === 'MENU') counts['MENU'] = (counts['MENU'] || 0) + 1;
      else if (row.resourceType === 'SCREEN') counts['SCREEN'] = (counts['SCREEN'] || 0) + 1;
      else if (row.resourceType !== 'ELEMENT') {
        // Business resources — count only top-level (no parent)
        if (!row.parentId) counts[row.resourceType] = (counts[row.resourceType] || 0) + 1;
      }
    }
    return counts;
  }, [flattenedData]);

  // Count draft (unsaved) entries per category for sidebar indicators
  // MODULE: count unique resources (1 toggle = 1 change, even though it sets N actions)
  // Others: count individual action entries (each toggle = 1 change)
  const categoryDraftCounts = useMemo((): Record<string, number> => {
    const counts: Record<string, number> = {};
    const seenModuleResources = new Set<string>();
    for (const key of draft.keys()) {
      const parts = key.split(':');
      const resourceType = parts[0];
      const resourceId = parts[1];
      const cat = resourceType === 'ELEMENT' ? 'SCREEN' : resourceType;
      if (cat === 'MODULE') {
        const resKey = `${resourceType}:${resourceId}`;
        if (!seenModuleResources.has(resKey)) {
          seenModuleResources.add(resKey);
          counts[cat] = (counts[cat] || 0) + 1;
        }
      } else {
        counts[cat] = (counts[cat] || 0) + 1;
      }
    }
    return counts;
  }, [draft]);

  // Draft count for the current category
  const currentCategoryDraftCount = categoryDraftCounts[selectedCategory] || 0;

  // Switch category — prompt if current category has unsaved changes
  const handleCategorySwitch = useCallback((newCategory: string) => {
    if (newCategory === selectedCategory) return;
    if (currentCategoryDraftCount > 0) {
      setConfirmDialog({ open: true, type: 'switch', targetCategory: newCategory });
      return;
    }
    setSelectedCategory(newCategory);
    if (newCategory !== 'MENU') setMenuModuleFilter('');
  }, [selectedCategory, currentCategoryDraftCount]);

  // Prompt before discard
  const handleDiscardClick = useCallback(() => {
    setConfirmDialog({ open: true, type: 'discard' });
  }, []);

  // Prompt before save
  const handleSaveClick = useCallback(() => {
    setConfirmDialog({ open: true, type: 'save' });
  }, []);

  // Confirm dialog action handler
  const handleConfirmAction = useCallback(() => {
    const { type, targetCategory } = confirmDialog;
    setConfirmDialog(prev => ({ ...prev, open: false }));
    if (type === 'switch') {
      // Discard current category changes, then switch
      clearCategoryDraft(selectedCategory);
      setSelectedCategory(targetCategory!);
      if (targetCategory !== 'MENU') setMenuModuleFilter('');
    } else if (type === 'discard') {
      clearCategoryDraft(selectedCategory);
    } else if (type === 'save') {
      handleSave();
    }
  }, [confirmDialog, selectedCategory, clearCategoryDraft, handleSave]);

  // Confirm dialog display props
  const confirmDialogProps = useMemo(() => {
    const catLabel = allCategories.find(c => c.key === selectedCategory)?.label || selectedCategory;
    switch (confirmDialog.type) {
      case 'switch':
        return {
          title: 'Unsaved Changes',
          message: `You have ${currentCategoryDraftCount} unsaved change${currentCategoryDraftCount !== 1 ? 's' : ''} in "${catLabel}". Discard and switch?`,
          confirmLabel: 'Discard & Switch',
          variant: 'danger' as const,
        };
      case 'discard':
        return {
          title: 'Discard Changes',
          message: `Discard unsaved changes in "${catLabel}"? This cannot be undone.`,
          confirmLabel: 'Discard',
          variant: 'danger' as const,
        };
      case 'save':
        return {
          title: 'Save Permissions',
          message: `Save "${catLabel}" permission changes?`,
          confirmLabel: mode === 'role' ? 'Save Permissions' : 'Save Overrides',
          variant: 'default' as const,
        };
    }
  }, [confirmDialog.type, mode, selectedCategory, allCategories, currentCategoryDraftCount]);

  // Filter flattened data by selected category
  const filteredData = useMemo((): PermissionRow[] => {
    if (flattenedData.length === 0) return [];
    const cat = selectedCategory;

    if (cat === 'MODULE') {
      // Only module rows (no children)
      return flattenedData.filter(r => r.resourceType === 'MODULE');
    }
    if (cat === 'MENU') {
      // Require module selection before showing menus
      if (!menuModuleFilter) return [];
      if (menuModuleFilter === MENU_MODULE_ALL) {
        // All modules: include module rows as grouping headers, menus as children
        const menus = flattenedData.filter(r => r.resourceType === 'MENU');
        const menuIds = new Set(menus.map(r => r.id));
        const moduleIdsWithMenus = new Set(menus.map(r => r.moduleId).filter(Boolean));
        const moduleRows = flattenedData.filter(r => r.resourceType === 'MODULE' && moduleIdsWithMenus.has(r.moduleId));
        return [
          ...moduleRows.map(r => ({ ...r, parentId: null })),
          ...menus.map(r => ({
            ...r,
            // Top-level menus re-parent to their module row; sub-menus keep their menu parent
            parentId: r.parentId && menuIds.has(r.parentId) ? r.parentId : `mod-${r.moduleId}`,
          })),
        ];
      }
      // Single module: menus become roots (no module grouping header)
      const menus = flattenedData.filter(r => r.resourceType === 'MENU' && r.moduleId === menuModuleFilter);
      const menuIds = new Set(menus.map(r => r.id));
      return menus.map(r => ({
        ...r,
        parentId: r.parentId && menuIds.has(r.parentId) ? r.parentId : null,
      }));
    }
    if (cat === 'SCREEN') {
      // Screens + their elements
      let screenRows = flattenedData.filter(r => r.resourceType === 'SCREEN' || r.resourceType === 'ELEMENT');
      // Screens become roots, elements keep screen parent
      const screenIds = new Set(screenRows.filter(r => r.resourceType === 'SCREEN').map(r => r.id));
      return screenRows.map(r => ({
        ...r,
        parentId: r.resourceType === 'SCREEN' ? null : (r.parentId && screenIds.has(r.parentId) ? r.parentId : null),
      }));
    }
    // Business resource categories — filter by resourceType, keep parentResourceId hierarchy
    const resRows = flattenedData.filter(r => r.resourceType === cat);
    return resRows;
  }, [flattenedData, selectedCategory, menuModuleFilter]);

  // Set of row IDs that are parents of other rows in the current filtered data
  // Used to hide action toggles on parent menus (leaf-only toggles)
  const parentRowIds = useMemo((): Set<string> => {
    const ids = new Set<string>();
    for (const row of filteredData) {
      if (row.parentId) ids.add(row.parentId);
    }
    return ids;
  }, [filteredData]);
  const parentRowIdsRef = useRef(parentRowIds);
  parentRowIdsRef.current = parentRowIds;

  // Refs so column cell renderers always use latest draft callbacks without re-creating columns
  const getEffectiveValueRef = useRef(getEffectiveValue);
  getEffectiveValueRef.current = getEffectiveValue;
  const setDraftValueRef = useRef(setDraftValue);
  setDraftValueRef.current = setDraftValue;

  // Column definitions
  const columns: TreeGridColumn<PermissionRow>[] = useMemo(() => {
    const cols: TreeGridColumn<PermissionRow>[] = [
      {
        id: 'resource',
        header: 'Resource',
        accessorKey: 'name',
        isTreeColumn: true,
        size: 360,
        cell: ({ row }: any) => {
          const r = row.original as PermissionRow;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', overflow: 'hidden' }}>
              {r.elementType && (
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem', flexShrink: 0 }}>[{r.elementType}]</span>
              )}
              <span style={{
                fontWeight: r.resourceType === 'MODULE' ? 700 : r.resourceType === 'MENU' ? 600 : 500,
                color: r.resourceType === 'MODULE' ? 'var(--primary)' : undefined,
              }}>
                {r.code}
              </span>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                — {r.name}
              </span>
            </div>
          );
        },
      },
    ];

    if (selectedCategory === 'MODULE') {
      // Single Grant/Revoke toggle for modules
      cols.push({
        id: 'grant',
        header: 'ACCESS',
        accessorKey: 'id',
        size: 100,
        cell: ({ row }: any) => {
          const r = row.original as PermissionRow;
          if (r.effActions) {
            const granted = r.effActions.some(a => a.effectiveValue === true);
            return <PermissionToggle checked={granted ? true : null} onChange={() => {}} disabled size="md" />;
          }
          if (r.stdActions && r.stdActions.length > 0) {
            const values = r.stdActions.map(a => getEffectiveValueRef.current(r.resourceType, r.resourceId, a.actionCode, r.stdActions as any));
            const isGranted = values.some(v => v === true);
            return (
              <PermissionToggle
                checked={isGranted ? true : null}
                onChange={(v: boolean | null) => {
                  for (const a of r.stdActions!) {
                    setDraftValueRef.current(r.resourceType, r.resourceId, a.actionCode, v, r.stdActions as any);
                  }
                }}
                size="md"
              />
            );
          }
          return null;
        },
      });
    } else {
      // Per-action toggle columns for other categories
      for (const actionCode of ALL_ACTION_CODES) {
        cols.push({
          id: actionCode,
          header: actionCode,
          accessorKey: 'id',
          size: 50,
          cell: ({ row }: any) => {
            const r = row.original as PermissionRow;
            const naIndicator = (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: '40px' }}>
                <span style={{ color: 'var(--text-disabled, #cbd5e1)', fontSize: '0.85rem' }}>—</span>
              </div>
            );
            // MODULE rows in MENU "All Modules" view are grouping headers — no action toggles
            if (r.resourceType === 'MODULE') {
              return null;
            }
            // MENU category: hide toggles on parent menus (only leaf menus show action toggles)
            if (r.resourceType === 'MENU' && parentRowIdsRef.current.has(r.id)) {
              return null;
            }
            if (r.effActions) {
              const entry = r.effActions.find(a => a.actionCode === actionCode);
              if (!entry) return naIndicator;
              const badge = getSourceBadge(entry.source, entry.sourceRoleNames);
              return <PermissionToggle checked={entry.effectiveValue} onChange={() => {}} disabled sourceBadge={badge} />;
            }
            if (r.stdActions) {
              const entry = r.stdActions.find(a => a.actionCode === actionCode);
              if (!entry) return naIndicator;
              const val = getEffectiveValueRef.current(r.resourceType, r.resourceId, actionCode, r.stdActions as any);
              return (
                <PermissionToggle
                  checked={val}
                  onChange={(v: boolean | null) => {
                    // VIEW toggle: block turning off if any other action is still enabled
                    if (r.resourceType === 'MENU' && actionCode === 'VIEW' && v !== true) {
                      const hasOtherEnabled = r.stdActions!.some(a => {
                        if (a.actionCode === 'VIEW') return false;
                        return getEffectiveValueRef.current(r.resourceType, r.resourceId, a.actionCode, r.stdActions as any) === true;
                      });
                      if (hasOtherEnabled) return; // Silently block — VIEW is required by other actions
                    }
                    setDraftValueRef.current(r.resourceType, r.resourceId, actionCode, v, r.stdActions as any);
                    // Auto-enable VIEW when any other action is granted on a menu item
                    if (r.resourceType === 'MENU' && actionCode !== 'VIEW' && v === true) {
                      const currentView = getEffectiveValueRef.current(r.resourceType, r.resourceId, 'VIEW', r.stdActions as any);
                      if (currentView !== true && r.stdActions!.some(a => a.actionCode === 'VIEW')) {
                        setDraftValueRef.current(r.resourceType, r.resourceId, 'VIEW', true, r.stdActions as any);
                      }
                    }
                  }}
                />
              );
            }
            return naIndicator;
          },
        });
      }
    }

    return cols;
  }, [selectedCategory]);

  // ============================================================================
  // RENDER
  // ============================================================================

  const selectorPlaceholder = mode === 'role'
    ? 'Select a role...'
    : mode === 'user-override'
      ? 'Select a user...'
      : 'Select a user...';
  const emptyMessage = mode === 'role'
    ? 'Select a role to view and manage its permissions.'
    : mode === 'user-override'
      ? 'Select a user to manage their permission overrides.'
      : 'Select a user to view their effective permissions.';

  return (
    <div className="module-master-container">
      {/* Responsive styles for permission sidebar */}
      <style>{`
        .perm-sidebar.config-group-sidebar {
          width: 280px;
          min-width: 240px;
          padding: 0;
          border: 1px solid var(--border-color, #e2e8f0);
        }
        .perm-controls-section {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid var(--border-color);
          display: flex;
          flex-direction: column;
          gap: 0.625rem;
        }
        .perm-sidebar-header {
          padding: 0.75rem 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--text-primary, #1e293b);
          background: var(--sidebar-header-bg, #e2e8f0);
          border-bottom: 2px solid var(--border-color, #e2e8f0);
          white-space: nowrap;
          user-select: none;
          flex-shrink: 0;
        }
        .perm-section-label {
          padding: 0.5rem 1rem 0.25rem;
          font-size: 0.65rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--text-secondary);
          background: var(--bg-secondary);
          border-bottom: 1px solid var(--border-color);
        }
        .perm-section-label--biz {
          padding-top: 0.625rem;
          border-top: 1px solid var(--border-color);
          margin-top: 0.25rem;
        }
        .perm-module-filter-sidebar {
          padding: 0.25rem 1rem 0.5rem;
        }
        .perm-module-filter-inline {
          display: none;
          padding: 0.5rem 1rem;
          border-bottom: 1px solid var(--border-color);
          background: var(--bg-secondary);
          flex-shrink: 0;
        }
        .perm-footer-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 1rem;
          border-top: 1px solid var(--border-color);
          background: var(--bg-secondary);
          flex-shrink: 0;
        }
        .perm-footer-actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .perm-footer-actions .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.375rem;
        }
        .perm-draft-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--warning, #f59e0b);
          flex-shrink: 0;
        }
        @media (max-width: 640px) {
          .perm-sidebar.config-group-sidebar {
            width: 100% !important;
            min-width: unset !important;
            flex-direction: column !important;
            flex-wrap: nowrap !important;
            overflow-x: visible !important;
            overflow-y: visible !important;
            border: none;
            border-bottom: 1px solid var(--border-color);
          }
          .perm-sidebar-header {
            display: none;
          }
          .perm-controls-section {
            flex-direction: row;
            flex-wrap: wrap;
            gap: 0.5rem;
          }
          .perm-controls-section > div {
            flex: 1;
            min-width: 140px;
          }
          .perm-category-nav {
            display: flex;
            flex-direction: row;
            overflow-x: auto;
            border-top: 1px solid var(--border-color);
          }
          .perm-category-nav .config-group-item {
            white-space: nowrap;
            border-left: none !important;
            border-bottom: 3px solid transparent;
            padding: 0.5rem 0.75rem;
            font-size: 0.8rem;
          }
          .perm-category-nav .config-group-item.active {
            border-left-color: transparent !important;
            border-bottom-color: var(--primary);
          }
          .perm-section-label {
            display: none;
          }
          .perm-module-filter-sidebar {
            display: none;
          }
          .perm-module-filter-inline {
            display: block;
          }
        }
      `}</style>
      <ModuleHeader
        title="Permission Assignment"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : undefined}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="config-settings-container" style={{ flex: 1, overflow: 'hidden' }}>
          {/* LEFT: Sidebar — controls + category navigation */}
          <div className="config-group-sidebar perm-sidebar">
            {/* Sidebar header — matches grid header height */}
            <div className="perm-sidebar-header">
              <IconSystem.settings size={16} />
              Permission Settings
            </div>

            {/* Mode toggle + Entity selector */}
            <div className="perm-controls-section">
              <div>
                <SyutSelect
                  options={MODE_SELECT_OPTIONS}
                  value={mode}
                  onChange={val => handleModeChange(val as ScreenMode)}
                  placeholder="Select mode..."
                  size="sm"
                />
              </div>

              <div>
                {mode === 'role' ? (
                  <SyutSelect
                    isAsync
                    loadOptions={loadRoleOptions}
                    defaultOptions
                    cacheOptions={false}
                    value={selectedRoleId}
                    onChange={val => setSelectedRoleId(val as string || '')}
                    placeholder={selectorPlaceholder}
                    isClearable
                    size="sm"
                  />
                ) : (
                  <SyutSelect
                    isAsync
                    loadOptions={loadUserOptions}
                    defaultOptions
                    cacheOptions={false}
                    value={selectedUserId}
                    onChange={val => setSelectedUserId(val as string || '')}
                    placeholder={selectorPlaceholder}
                    isClearable
                    size="sm"
                  />
                )}
              </div>

              {isEffectiveMode && effectiveData && (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', lineHeight: 1.3 }}>
                  Roles: {effectiveData.roles.map(r => r.roleName).join(', ') || 'None assigned'}
                </div>
              )}
            </div>

            {/* Category navigation (only when entity is selected) */}
            {hasSelection && (
              <div className="perm-category-nav">
                <div className="perm-section-label">
                  UI Resources
                </div>
                {allCategories.filter(c => c.group === 'ui').map(cat => {
                  const IconComp = (IconSystem as any)[cat.icon];
                  return (
                    <React.Fragment key={cat.key}>
                      <div
                        className={`config-group-item${selectedCategory === cat.key ? ' active' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleCategorySwitch(cat.key)}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCategorySwitch(cat.key); } }}
                      >
                        {IconComp && <span className="config-group-icon"><IconComp size={16} /></span>}
                        <span style={{ flex: 1 }}>{cat.label}</span>
                        {categoryDraftCounts[cat.key] > 0 && <span className="perm-draft-dot" />}
                        {categoryCounts[cat.key] != null && (
                          <span style={{ fontSize: '0.7rem', color: 'var(--text-disabled)', fontWeight: 500 }}>
                            {categoryCounts[cat.key]}
                          </span>
                        )}
                      </div>
                      {/* Module filter dropdown — shown below Menus when selected */}
                      {cat.key === 'MENU' && selectedCategory === 'MENU' && moduleOptions.length > 0 && (
                        <div className="perm-module-filter-sidebar">
                          <SyutSelect
                            options={moduleOptions}
                            value={menuModuleFilter}
                            onChange={val => setMenuModuleFilter(val as string || '')}
                            placeholder="Select module..."
                            isClearable
                            size="sm"
                          />
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Business Resources section (only if any exist) */}
                {allCategories.some(c => c.group === 'business') && (
                  <>
                    <div className="perm-section-label perm-section-label--biz">
                      Business Resources
                    </div>
                    {allCategories.filter(c => c.group === 'business').map(cat => {
                      const IconComp = (IconSystem as any)[cat.icon];
                      return (
                        <div
                          key={cat.key}
                          className={`config-group-item${selectedCategory === cat.key ? ' active' : ''}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleCategorySwitch(cat.key)}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCategorySwitch(cat.key); } }}
                        >
                          {IconComp && <span className="config-group-icon"><IconComp size={16} /></span>}
                          <span style={{ flex: 1 }}>{cat.label}</span>
                          {categoryDraftCounts[cat.key] > 0 && <span className="perm-draft-dot" />}
                          {categoryCounts[cat.key] != null && (
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-disabled)', fontWeight: 500 }}>
                              {categoryCounts[cat.key]}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>

          {/* RIGHT: Content Panel */}
          <div className="config-settings-panel">
            {/* Mobile-only module filter (shown at ≤640px when MENU selected) */}
            {hasSelection && selectedCategory === 'MENU' && moduleOptions.length > 0 && (
              <div className="perm-module-filter-inline">
                <SyutSelect
                  options={moduleOptions}
                  value={menuModuleFilter}
                  onChange={val => setMenuModuleFilter(val as string || '')}
                  placeholder="Select module..."
                  isClearable
                  size="sm"
                />
              </div>
            )}

            {!hasSelection ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                {emptyMessage}
              </div>
            ) : selectedCategory === 'MENU' && !menuModuleFilter ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                Select a module to view its menus.
              </div>
            ) : (
              <>
                <div className="data-grid-container" style={{ flex: 1, minHeight: 0 }}>
                  <SyutTreeGrid<PermissionRow>
                    key={`${selectedCategory}-${menuModuleFilter}`}
                    data={filteredData}
                    columns={columns}
                    parentKey="parentId"
                    sortKey="sortOrder"
                    showNodeIcons={false}
                    defaultExpandLevel={selectedCategory === 'SCREEN' ? 0 : -1}
                    isLoading={loadingTree}
                    emptyMessage={
                      selectedCategory === 'MODULE' ? 'No modules registered.'
                      : selectedCategory === 'MENU' ? 'No menus found for this module.'
                      : selectedCategory === 'SCREEN' ? 'No screens found.'
                      : `No ${(allCategories.find(c => c.key === selectedCategory)?.label || 'resources').toLowerCase()} found.`
                    }
                  />
                </div>
              </>
            )}

            {/* Save/Discard footer bar — current category draft count */}
            {currentCategoryDraftCount > 0 && !isEffectiveMode && (
              <div className="perm-footer-bar">
                <span style={{ fontSize: '0.75rem', color: 'var(--warning)', fontWeight: 600 }}>
                  ● {currentCategoryDraftCount} unsaved change{currentCategoryDraftCount !== 1 ? 's' : ''}
                </span>
                <div className="perm-footer-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleDiscardClick}
                    disabled={saving}
                    style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}
                  >
                    Discard
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveClick}
                    disabled={saving}
                    style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}
                  >
                    <IconSystem.save size={12} />
                    {saving ? 'Saving...' : mode === 'role' ? 'Save Permissions' : 'Save Overrides'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialogProps.title}
        message={confirmDialogProps.message}
        confirmLabel={confirmDialogProps.confirmLabel}
        cancelLabel="Cancel"
        variant={confirmDialogProps.variant}
        loading={confirmDialog.type === 'save' && saving}
        loadingMessage="Saving..."
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default PermissionAssignmentScreen;
